import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  Sparkles,
  Archive,
  Calendar,
  Target,
  CalendarClock,
  Users,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EnhancedLeadCard } from '../EnhancedLeadCard';
import { LeadWithMetrics, DashboardTab } from '@/types/dashboard';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface TabConfig {
  id: DashboardTab;
  label: string;
  icon: React.ElementType;
  endpoint: string;
  color: string;
}

const TAB_CONFIGS: TabConfig[] = [
  {
    id: 'total-pipeline',
    label: 'Total Pipeline',
    icon: TrendingUp,
    endpoint: '/lead-metrics/dashboard/total-pipeline',
    color: 'blue'
  },
  {
    id: 'fresh-pipeline',
    label: 'Fresh Pipeline',
    icon: Sparkles,
    endpoint: '/lead-metrics/dashboard/fresh-pipeline',
    color: 'green'
  },
  {
    id: 'old-pipeline',
    label: 'Old Pipeline',
    icon: Archive,
    endpoint: '/lead-metrics/dashboard/old-pipeline',
    color: 'gray'
  },
  {
    id: 'today-followup',
    label: "Today's Follow-up",
    icon: Calendar,
    endpoint: '/lead-metrics/dashboard/todays-followup', // Fixed: matches backend route
    color: 'orange'
  },
  {
    id: 'closure-week',
    label: 'Closing This Week',
    icon: Target,
    endpoint: '/lead-metrics/dashboard/week-closure', // Fixed: matches backend route
    color: 'purple'
  },
  {
    id: 'closure-month',
    label: 'Closing This Month',
    icon: CalendarClock,
    endpoint: '/lead-metrics/dashboard/month-closure', // Fixed: matches backend route
    color: 'indigo'
  },
  {
    id: 'meetings-today',
    label: 'Meetings Today',
    icon: Users,
    endpoint: '/lead-metrics/dashboard/meetings-today',
    color: 'pink'
  },
  {
    id: 'meetings-done',
    label: 'Meetings Done',
    icon: CheckCircle2,
    endpoint: '/lead-metrics/dashboard/meetings-done',
    color: 'teal'
  }
];

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

export interface PipelineFilters {
  search?: string;
  stage?: string;    // comma-separated stages e.g. "NEW,CONTACTED"
  priority?: string; // comma-separated priorities e.g. "HOT,WARM"
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string;   // YYYY-MM-DD
}

interface DashboardTabsContainerProps {
  className?: string;
  filters?: PipelineFilters;
}

export const DashboardTabsContainer: React.FC<DashboardTabsContainerProps> = ({ className = '', filters }) => {
  const [activeTab, setActiveTab] = useState<DashboardTab>('total-pipeline');
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch data for the active tab
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard-tab', activeTab, filters],
    queryFn: async (): Promise<{ leads: LeadWithMetrics[]; count: number; totalValue: number }> => {
      const config = TAB_CONFIGS.find(t => t.id === activeTab);
      if (!config) throw new Error('Invalid tab');

      // Build query string from filters
      const params = new URLSearchParams();
      if (filters?.search) params.set('search', filters.search);
      if (filters?.stage) params.set('stage', filters.stage);
      if (filters?.priority) params.set('priority', filters.priority);
      if (filters?.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters?.dateTo) params.set('dateTo', filters.dateTo);
      const qs = params.toString();
      const url = `${API_BASE_URL}${config.endpoint}${qs ? `?${qs}` : ''}`;

      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      if (response.status === 403) {
        throw new Error('You don\'t have permission for this action');
      }
      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const result = await response.json();

      // Handle API response structure: { success: true, data: { leads, count, totalValue } }
      // Extract the inner data object to maintain component compatibility
      if (result.success && result.data) {
        return result.data;
      }

      // Fallback for direct response (legacy support)
      return result;
    },
    refetchInterval: 60000, // Auto-refresh every 60 seconds
    staleTime: 30000 // Consider data stale after 30 seconds
  });

  const handleViewAll = () => {
    navigate('/pipeline');
  };

  const handleLeadAction = (lead: LeadWithMetrics, action: 'view' | 'edit' | 'call' | 'email') => {
    switch (action) {
      case 'view':
        navigate('/pipeline', { state: { selectedLeadId: lead.id } });
        break;
      case 'edit':
        navigate('/pipeline', { state: { selectedLeadId: lead.id, editMode: true } });
        break;
      case 'call':
        if (lead.phoneNumber) {
          window.location.href = `tel:${lead.phoneNumber}`;
        } else {
          toast({
            title: 'No phone number',
            description: 'This lead does not have a phone number',
            variant: 'destructive'
          });
        }
        break;
      case 'email':
        if (lead.email) {
          window.location.href = `mailto:${lead.email}`;
        } else {
          toast({
            title: 'No email address',
            description: 'This lead does not have an email address',
            variant: 'destructive'
          });
        }
        break;
    }
  };

  const currentConfig = TAB_CONFIGS.find(t => t.id === activeTab);

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Pipeline Overview</h2>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleViewAll}
            className="bg-blue-600 hover:bg-blue-700"
          >
            View All Leads
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as DashboardTab)} className="w-full">
        <TabsList className="grid grid-cols-4 lg:grid-cols-8 gap-2 h-auto bg-gray-100 p-2 rounded-lg">
          {TAB_CONFIGS.map((config) => {
            const Icon = config.icon;
            return (
              <TabsTrigger
                key={config.id}
                value={config.id}
                className="flex flex-col items-center gap-1.5 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md"
              >
                <Icon className="h-4 w-4" />
                <span className="text-xs font-medium text-center leading-tight">{config.label}</span>
                {activeTab === config.id && data && (
                  <Badge variant="secondary" className="mt-1 text-xs">
                    {data.count}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {TAB_CONFIGS.map((config) => (
          <TabsContent key={config.id} value={config.id} className="mt-6">
            <AnimatePresence mode="wait">
              {isLoading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center py-12"
                >
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-4" />
                  <p className="text-gray-600">Loading leads...</p>
                </motion.div>
              ) : error ? (
                <motion.div
                  key="error"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center py-12"
                >
                  <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                  <p className="text-gray-900 font-medium mb-2">Failed to load leads</p>
                  <p className="text-gray-600 text-sm mb-4">
                    {error instanceof Error ? error.message : 'Please try again'}
                  </p>
                  <Button variant="outline" onClick={() => refetch()}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retry
                  </Button>
                </motion.div>
              ) : (!data || !Array.isArray(data.leads) || data.leads.length === 0) ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="flex flex-col items-center justify-center py-12"
                >
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    {React.createElement(config.icon, { className: 'h-8 w-8 text-gray-400' })}
                  </div>
                  <p className="text-gray-900 font-medium mb-2">No leads found</p>
                  <p className="text-gray-600 text-sm">No leads match this criteria</p>
                </motion.div>
              ) : (
                <motion.div
                  key="content"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-4"
                >
                  {/* Summary Card */}
                  <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Total Leads</p>
                        <p className="text-3xl font-bold text-gray-900">{data?.count || 0}</p>
                      </div>
                      {data?.totalValue !== undefined && (
                        <div className="text-right">
                          <p className="text-sm text-gray-600 mb-1">Total Value</p>
                          <p className="text-2xl font-bold text-blue-600">
                            {new Intl.NumberFormat('en-IN', {
                              style: 'currency',
                              currency: 'INR',
                              minimumFractionDigits: 0
                            }).format(data.totalValue)}
                          </p>
                        </div>
                      )}
                    </div>
                  </Card>

                  {/* Leads Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {(data?.leads || []).map((lead) => (
                      <EnhancedLeadCard
                        key={lead.id}
                        lead={lead}
                        onViewDetails={(l) => handleLeadAction(l, 'view')}
                        onEdit={(l) => handleLeadAction(l, 'edit')}
                        onCall={(l) => handleLeadAction(l, 'call')}
                        onEmail={(l) => handleLeadAction(l, 'email')}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
